import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';

const STORAGE_KEY = 'jerico_nav_pins';
const MAX_PINS = 8;

const DEFAULT_PINS: Record<string, string[]> = {
  owner: ['/app', '/app/matters', '/app/clients', '/app/invoices', '/app/analytics/revenue', '/app/reports', '/app/calendar', '/app/users'],
  admin: ['/app', '/app/matters', '/app/clients', '/app/invoices', '/app/users', '/app/settings', '/app/calendar', '/app/audit'],
  lawyer: ['/app', '/app/matters', '/app/timesheet', '/app/documents', '/app/calendar', '/app/ai-chat', '/app/emails', '/app/deadlines'],
  paralegal: ['/app', '/app/tasks', '/app/documents', '/app/deadlines', '/app/calendar', '/app/emails', '/app/timesheet'],
  system_admin: ['/app', '/app/matters', '/app/clients', '/app/users', '/app/settings', '/app/audit'],
};

export function useNavPins() {
  const { user } = useAuth();

  const [pinnedPaths, setPinnedPaths] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return DEFAULT_PINS[user?.role || 'lawyer'] || DEFAULT_PINS.lawyer;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pinnedPaths));
    } catch { /* ignore */ }
  }, [pinnedPaths]);

  const isPinned = useCallback((path: string) => pinnedPaths.includes(path), [pinnedPaths]);

  const togglePin = useCallback((path: string) => {
    setPinnedPaths(prev => {
      if (prev.includes(path)) {
        return prev.filter(p => p !== path);
      }
      if (prev.length >= MAX_PINS) return prev; // enforce max
      return [...prev, path];
    });
  }, []);

  return { pinnedPaths, isPinned, togglePin };
}
